import { takeLatest, all, put, call, select } from 'redux-saga/effects';

import axios from '../../axiosInstance';
import requests from '../../requests';

import { authActionTypes } from '../auth/auth.types';
import { workspaceActionTypes } from './workspace.types';

import { showNotify } from '../notify/notify.actions';
import { updateSessionStatus } from '../auth/auth.actions';

import {
    workspaceInitRequest,
    workspaceInitSuccess,
    workspaceInitFailure,
    workspaceSwitchSuccess,
    workspaceSwitchFailure,
    workspaceFetchTeamsSuccess,
    workspaceFetchTeamsFailure,
    workspaceFetchMembersSuccess,
    workspaceFetchMembersFailure,
    workspaceReset,
    workspaceCreateSuccess,
    workspaceCreateFailure,
    workspaceInviteMemberRequest,
    workspaceInviteMemberSuccess,
    workspaceInviteMemberFailure,
    workspaceUpdateSuccess,
    workspaceUpdateFailure,
    workspaceLeaveSuccess,
    workspaceLeaveFailure,
    workspaceArchiveSuccess,
    workspaceArchiveFailure,
    workspaceFetchMembersRequest,
    workspaceRemoveMemberSuccess,
    workspaceRemoveMemberFailure,
    workspaceFetchInvitesRequest,
    workspaceFetchInvitesSuccess,
    workspaceFetchInvitesFailure,
    workspaceRevokeInviteSuccess,
    workspaceRevokeInviteFailure,
} from './workspace.actions';

import { enablePricingPlanLimit } from '../main/main.actions';
import { resolvePlanErrorI18nKey } from '../../lib/plan-errors';

import { getPlanRestrictions } from './workspace.plans';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pickActiveWorkspace(list, lastActiveId) {
    if (lastActiveId) {
        const found = list.find((w) => w.id === lastActiveId);
        if (found) return found;
    }
    return list[0] || null;
}

// ---------------------------------------------------------------------------
// Sagas
// ---------------------------------------------------------------------------

// Called after every successful sign-in or session check.
// Fetches workspaces, resolves the active one (from auth.lastActiveWorkspaceId),
// and derives plan restrictions.
export function* initWorkspace() {
    yield put(workspaceInitRequest());
    try {
        const { data } = yield axios.get(requests.workspaces);
        const list = data || [];

        const lastActiveId = yield select((state) => state.auth.lastActiveWorkspaceId);
        const currentWorkspace = pickActiveWorkspace(list, lastActiveId);

        const planName = currentWorkspace?.plan_slug || 'free';
        const restrictions = getPlanRestrictions(planName);

        yield put(workspaceInitSuccess({
            list,
            currentWorkspace,
            plan: planName,
            restrictions,
        }));

        // If we picked a fallback (server had no last_active OR it pointed to a
        // workspace the user no longer belongs to), persist the choice to the
        // server so other endpoints' middleware sees it. Best-effort, silent.
        if (currentWorkspace && currentWorkspace.id !== lastActiveId) {
            try {
                yield axios.put(requests.userActiveWorkspace, { workspaceId: currentWorkspace.id });
            } catch (_) {
                // Non-critical — UI still shows the chosen workspace.
            }
        }
    } catch (e) {
        yield put(workspaceInitFailure(e));
    }
}

// Switch to a different workspace (picked from the already-fetched list).
// Pessimistic: only commits to redux on a 200 from PUT.
export function* switchWorkspace({ payload: workspaceId }) {
    try {
        const list = yield select((state) => state.workspace.list);
        const workspace = list.find((w) => w.id === workspaceId);
        if (!workspace) return;

        // No-op switch — clear pendingSwitchId via the failure path so the
        // menu's spinner row resets, but skip the PUT and the success path
        // (which would otherwise clear teams/members for no reason).
        const currentId = yield select((s) => s.workspace.currentWorkspace?.id);
        if (currentId === workspaceId) {
            yield put(workspaceSwitchFailure(null));
            return;
        }

        // Send to server FIRST. Other slices (diagrams, teams, members) re-fetch
        // when currentWorkspace changes, so we don't want to flip the UI then
        // get a 403 and have to roll back stale data.
        yield axios.put(requests.userActiveWorkspace, { workspaceId });

        const planName = workspace.plan_slug || 'free';
        const restrictions = getPlanRestrictions(planName);

        yield put(workspaceSwitchSuccess({
            currentWorkspace: workspace,
            plan: planName,
            restrictions,
        }));
    } catch (e) {
        yield put(workspaceSwitchFailure(e));
        const status = e?.response?.status;
        if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
        } else if (status === 403) {
            yield put(showNotify({ message: 'not_workspace_member' }));
        } else {
            yield put(showNotify({ message: 'workspace_switch_failed' }));
        }
    }
}

// Fetch teams for the given workspace.
export function* fetchWorkspaceTeams({ payload: workspaceId }) {
    try {
        const url = `${requests.workspaces}/${workspaceId}/teams`;
        const { data } = yield axios.get(url);
        yield put(workspaceFetchTeamsSuccess(data?.data || []));
    } catch (e) {
        yield put(workspaceFetchTeamsFailure(e));
    }
}

// Fetch members for the given workspace.
export function* fetchWorkspaceMembers({ payload: workspaceId }) {
    try {
        const url = `${requests.workspaces}/${workspaceId}/members`;
        const { data } = yield axios.get(url);
        yield put(workspaceFetchMembersSuccess(data || []));
    } catch (e) {
        yield put(workspaceFetchMembersFailure(e));
    }
}

// Clear workspace state on sign-out.
export function* resetWorkspaceState() {
    yield put(workspaceReset());
}

// Create workspace, refetch the list, activate it, optionally invite, then
// invoke onSuccess so the modal can advance. Callback-style; no Redux flag.
export function* createWorkspace({ payload }) {
    const { title, email, onSuccess, onError } = payload || {};
    try {
        // 1. POST -- create the workspace. Backend inserts the caller as owner
        //    in the same transaction.
        const { data: created } = yield axios.post(requests.workspaces, { title });
        yield put(workspaceCreateSuccess(created));

        // 2. GET -- refetch the list so `my_role` is authoritative and the new
        //    workspace appears in state. Reuses WORKSPACE_INIT_SUCCESS (which
        //    already replaces list + currentWorkspace + plan + restrictions).
        const { data: list } = yield axios.get(requests.workspaces);
        const newWorkspace = (list || []).find((w) => w.id === created.id) || created;
        const planName = newWorkspace.plan_slug || 'free';
        const restrictions = getPlanRestrictions(planName);
        yield put(workspaceInitSuccess({
            list: list || [],
            currentWorkspace: newWorkspace,
            plan: planName,
            restrictions,
        }));

        // 3. PUT -- persist the active workspace server-side so future bootstraps
        //    land here. Dispatch WORKSPACE_SWITCH_SUCCESS afterwards to clear
        //    stale teams/members (the init success doesn't touch those slices).
        yield axios.put(requests.userActiveWorkspace, { workspaceId: created.id });
        yield put(workspaceSwitchSuccess({
            currentWorkspace: newWorkspace,
            plan: planName,
            restrictions,
        }));

        // 4. Optional invite -- fire-and-forget. Errors here do not fail the chain.
        if (email && email.trim()) {
            yield put(workspaceInviteMemberRequest({ workspaceId: created.id, email: email.trim() }));
        }

        // 5. Tell the modal we're done so it can advance to step 3.
        if (typeof onSuccess === 'function') onSuccess(newWorkspace);
    } catch (e) {
        yield put(workspaceCreateFailure(e));
        const status = e?.response?.status;
        const body = e?.response?.data;

        if (status === 402) {
            if (body?.message === 'plan_limit_reached') {
                const { current, max } = body?.data || {};
                yield put(enablePricingPlanLimit({ type: 'workspaces', current, max }));
            } else {
                yield put(showNotify({ message: resolvePlanErrorI18nKey(body?.message) }));
            }
            if (typeof onError === 'function') onError({ kind: 'plan_limit' });
        } else if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
            if (typeof onError === 'function') onError({ kind: 'auth' });
        } else if (status === 400) {
            const msg = body?.details?.[0]?.message || body?.message || 'Invalid workspace name';
            yield put(showNotify({ message: 'invalid_workspace_name' }));
            if (typeof onError === 'function') onError({ kind: 'validation', message: msg });
        } else {
            yield put(showNotify({ message: 'workspace_create_failed' }));
            if (typeof onError === 'function') onError({ kind: 'generic' });
        }
    }
}

// Invite a user to a workspace by email. Called from the workspace-create
// chain when the user supplies a teammate email at creation time. The modal
// flow lives in redux/main/main.sagas.js `invitePeopleRequest` (which surfaces
// per-status notifications); this one is best-effort inside the chain.
export function* inviteWorkspaceMember({ payload }) {
    const { workspaceId, email } = payload || {};
    try {
        if (!workspaceId || !email) {
            yield put(workspaceInviteMemberFailure(new Error('missing_workspace_or_email')));
            return;
        }
        const url = `${requests.workspaces}/${workspaceId}/invites`;
        const { data } = yield axios.post(url, { email });
        yield put(workspaceInviteMemberSuccess({ workspaceId, email, inviteId: data?.id }));
    } catch (e) {
        yield put(workspaceInviteMemberFailure(e));
    }
}

// Update workspace fields (currently used for title + short_name from the
// Settings → Workspace page). Callback-style so the UI can revert local
// state on failure.
export function* updateWorkspace({ payload }) {
    const { id, fields, onSuccess, onError } = payload || {};
    if (!id) {
        yield put(workspaceUpdateFailure(new Error('missing workspace id')));
        return;
    }
    try {
        const url = `${requests.workspaces}/${id}`;
        yield axios.patch(url, fields || {});
        yield put(workspaceUpdateSuccess({ id, fields: fields || {} }));
        yield put(showNotify({ message: 'workspace_updated' }));
        if (typeof onSuccess === 'function') onSuccess();
    } catch (e) {
        yield put(workspaceUpdateFailure(e));
        yield put(showNotify({ message: 'workspace_update_failed' }));
        if (typeof onError === 'function') onError(e);
    }
}

// Move the user off a workspace they just left/archived. Computes the remaining
// set here (don't rely on reducer ordering) so it is self-contained. If another
// workspace remains and we left the active one, re-point the active workspace;
// otherwise re-bootstrap (currentWorkspace → null). Shared by leave + archive.
function* switchAfterLeavingWorkspace(id) {
    const list = yield select((s) => s.workspace.list);
    const currentId = yield select((s) => s.workspace.currentWorkspace?.id);
    const remaining = (list || []).filter((w) => w.id !== id);

    if (remaining.length > 0) {
        // Only re-point the active workspace if we actually left the active one.
        if (currentId === id || !currentId) {
            const next = remaining[0];
            try {
                yield axios.put(requests.userActiveWorkspace, { workspaceId: next.id });
            } catch (_) {
                // Non-critical — UI still switches to the chosen workspace.
            }
            const planName = next.plan_slug || 'free';
            const restrictions = getPlanRestrictions(planName);
            yield put(workspaceSwitchSuccess({
                currentWorkspace: next,
                plan: planName,
                restrictions,
            }));
        }
    } else {
        yield call(initWorkspace);
    }
}

// Leave the current workspace (caller removes their own membership), then move
// the user to another workspace. Callback-style so the modal can close.
// Owners are blocked in the UI, so the 409 last-owner guard is defensive only.
export function* leaveWorkspace({ payload }) {
    const { id, onSuccess, onError } = payload || {};
    if (!id) {
        const err = new Error('missing workspace id');
        yield put(workspaceLeaveFailure(err));
        if (typeof onError === 'function') onError(err);
        return;
    }
    try {
        const url = `${requests.workspaces}/${id}/leave`;
        yield axios.post(url);
        yield put(workspaceLeaveSuccess(id));

        yield call(switchAfterLeavingWorkspace, id);

        if (typeof onSuccess === 'function') onSuccess();
    } catch (e) {
        yield put(workspaceLeaveFailure(e));
        const status = e?.response?.status;
        const body = e?.response?.data;

        if (status === 409 && body?.message === 'last_owner_cannot_leave') {
            yield put(showNotify({ message: 'last_owner_cannot_leave' }));
        } else if (status === 404) {
            // Stale UI — the caller already isn't a member. Refresh the list.
            yield call(initWorkspace);
        } else if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
        } else {
            yield put(showNotify({ message: 'workspace_leave_failed' }));
        }
        if (typeof onError === 'function') onError(e);
    }
}

// Archive the current workspace (owner-only), then move the user to another
// workspace. Backs the "delete workspace" UX — the backend schedules permanent
// deletion from the archived state. Callback-style so the modal can close.
export function* archiveWorkspace({ payload }) {
    const { id, onSuccess, onError } = payload || {};
    if (!id) {
        const err = new Error('missing workspace id');
        yield put(workspaceArchiveFailure(err));
        if (typeof onError === 'function') onError(err);
        return;
    }
    try {
        // const url = `${requests.workspaces}/${id}/archive`;
        const url = `${requests.workspaces}/${id}`;
        yield axios.delete(url);
        yield put(workspaceArchiveSuccess(id));

        yield call(switchAfterLeavingWorkspace, id);

        yield put(showNotify({ message: 'workspace_deleted' }));

        if (typeof onSuccess === 'function') onSuccess();
    } catch (e) {
        yield put(workspaceArchiveFailure(e));
        const status = e?.response?.status;
        const body = e?.response?.data;

        if (status === 403) {
            yield put(showNotify({ message: 'workspace_delete_forbidden' }));
        } else if (status === 409 && body?.message === 'workspace_has_active_billing') {
            yield put(showNotify({ message: 'workspace_has_active_billing' }));
        } else if (status === 404) {
            // Stale UI — caller isn't a member or the workspace is gone. Refresh.
            yield call(initWorkspace);
        } else if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
        } else {
            yield put(showNotify({ message: 'workspace_delete_failed' }));
        }
        if (typeof onError === 'function') onError(e);
    }
}

// Remove another member from the workspace (owner/admin). Callback-style so the
// modal can close. On success the member is dropped from the list optimistically.
// `userId` is the target's users.id — the endpoint is DELETE …/members/:userId
// (see members-management-frontend-integration.md §4), not the membership-row id.
export function* removeWorkspaceMember({ payload }) {
    const { workspaceId, userId, onSuccess, onError } = payload || {};
    if (!workspaceId || !userId) {
        const err = new Error('missing workspace or user id');
        yield put(workspaceRemoveMemberFailure(err));
        if (typeof onError === 'function') onError(err);
        return;
    }
    try {
        const url = `${requests.workspaces}/${workspaceId}/members/${userId}`;
        yield axios.delete(url);
        yield put(workspaceRemoveMemberSuccess(userId));
        yield put(showNotify({ message: 'member_removed' }));
        if (typeof onSuccess === 'function') onSuccess();
    } catch (e) {
        yield put(workspaceRemoveMemberFailure(e));
        const status = e?.response?.status;
        const message = e?.response?.data?.message;

        if (status === 403) {
            yield put(showNotify({ message: 'member_remove_forbidden' }));
        } else if (status === 409 && message === 'cannot_remove_self') {
            // Should never reach here — the UI offers Leave on your own row.
            yield put(showNotify({ message: 'cannot_remove_self' }));
        } else if (status === 409 && message === 'cannot_remove_last_owner') {
            yield put(showNotify({ message: 'cannot_remove_last_owner' }));
        } else if (status === 404) {
            // Stale UI — already removed or workspace gone. Refresh both lists.
            yield put(workspaceFetchMembersRequest(workspaceId));
            yield put(workspaceFetchInvitesRequest(workspaceId));
        } else if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
        } else {
            yield put(showNotify({ message: 'member_remove_failed' }));
        }
        if (typeof onError === 'function') onError(e);
    }
}

// Fetch pending invites for the given workspace (owner/admin only — others get
// 403/404, which just yields an empty list in the UI). GET returns a bare array.
export function* fetchWorkspaceInvites({ payload: workspaceId }) {
    try {
        const url = `${requests.workspaces}/${workspaceId}/invites`;
        const { data } = yield axios.get(url);
        yield put(workspaceFetchInvitesSuccess(data || []));
    } catch (e) {
        yield put(workspaceFetchInvitesFailure(e));
    }
}

// Revoke a pending invite (owner/admin). Callback-style so the UI can revert an
// optimistic drop on failure. 404 means "already gone" — treat as success.
export function* revokeWorkspaceInvite({ payload }) {
    const { workspaceId, inviteId, onSuccess, onError } = payload || {};
    if (!workspaceId || !inviteId) {
        const err = new Error('missing workspace or invite id');
        yield put(workspaceRevokeInviteFailure(err));
        if (typeof onError === 'function') onError(err);
        return;
    }
    try {
        const url = `${requests.workspaces}/${workspaceId}/invites/${inviteId}`;
        yield axios.delete(url);
        yield put(workspaceRevokeInviteSuccess(inviteId));
        yield put(showNotify({ message: 'invite_removed' }));
        if (typeof onSuccess === 'function') onSuccess();
    } catch (e) {
        const status = e?.response?.status;

        // Already revoked / gone — the desired end state is already true.
        if (status === 404) {
            yield put(workspaceRevokeInviteSuccess(inviteId));
            if (typeof onSuccess === 'function') onSuccess();
            return;
        }

        yield put(workspaceRevokeInviteFailure(e));
        if (status === 403) {
            yield put(showNotify({ message: 'invite_revoke_forbidden' }));
        } else if (status === 401) {
            yield put(updateSessionStatus('not_authorized'));
        } else {
            yield put(showNotify({ message: 'invite_revoke_failed' }));
        }
        if (typeof onError === 'function') onError(e);
    }
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

export function* onSignInSuccess() {
    yield takeLatest(authActionTypes.SIGN_IN_SUCCESS, initWorkspace);
}

export function* onWorkspaceSwitchRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_SWITCH_REQUEST, switchWorkspace);
}

export function* onWorkspaceFetchTeamsRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_FETCH_TEAMS_REQUEST, fetchWorkspaceTeams);
}

export function* onWorkspaceFetchMembersRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_REQUEST, fetchWorkspaceMembers);
}

export function* onSignOutSuccess() {
    yield takeLatest(authActionTypes.SIGN_OUT_SUCCESS, resetWorkspaceState);
}

export function* onWorkspaceCreateRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_CREATE_REQUEST, createWorkspace);
}

export function* onWorkspaceInviteMemberRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_INVITE_MEMBER_REQUEST, inviteWorkspaceMember);
}

export function* onWorkspaceUpdateRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_UPDATE_REQUEST, updateWorkspace);
}

export function* onWorkspaceLeaveRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_LEAVE_REQUEST, leaveWorkspace);
}

export function* onWorkspaceArchiveRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_ARCHIVE_REQUEST, archiveWorkspace);
}

export function* onWorkspaceRemoveMemberRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_REMOVE_MEMBER_REQUEST, removeWorkspaceMember);
}

export function* onWorkspaceFetchInvitesRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_FETCH_INVITES_REQUEST, fetchWorkspaceInvites);
}

export function* onWorkspaceRevokeInviteRequest() {
    yield takeLatest(workspaceActionTypes.WORKSPACE_REVOKE_INVITE_REQUEST, revokeWorkspaceInvite);
}

export function* workspaceSagas() {
    yield all([
        call(onSignInSuccess),
        call(onWorkspaceSwitchRequest),
        call(onWorkspaceFetchTeamsRequest),
        call(onWorkspaceFetchMembersRequest),
        call(onWorkspaceCreateRequest),
        call(onWorkspaceInviteMemberRequest),
        call(onWorkspaceUpdateRequest),
        call(onWorkspaceLeaveRequest),
        call(onWorkspaceArchiveRequest),
        call(onWorkspaceRemoveMemberRequest),
        call(onWorkspaceFetchInvitesRequest),
        call(onWorkspaceRevokeInviteRequest),
        call(onSignOutSuccess),
    ]);
}
